// ---------------------------------------------------------------------------
// Prezio Onboard Widget — Hosted page parameters
// ---------------------------------------------------------------------------
// The hosted page (widget.prezio.eu) is configured entirely through query
// params, and it is the path every iframe embed takes. Parsing lives here
// rather than inline in index.html so that it can be unit tested.
// ---------------------------------------------------------------------------

import type { Locale, ThemeConfig } from '@/types/config';
import { sanitizeColorScheme, type WidgetRemoteConfig } from '@/utils/configSanitizer';

/** Build-time values from `import.meta.env`, used when a param is absent (local dev / E2E). */
export interface HostedPageFallbacks {
  token?: string | undefined;
  sessionId?: string | undefined;
  apiBaseUrl?: string | undefined;
}

/** Everything the hosted page can learn from its own URL, ready to spread into `init()`. */
export interface HostedPageParams {
  sessionToken: string;
  sessionId?: string;
  apiBaseUrl?: string;
  /** Absent when the URL has none, so `init()` falls back to the browser language. */
  locale?: Locale;
  theme: ThemeConfig;
  demoMode?: boolean;
  upgradeUrl?: string;
  remoteConfig?: WidgetRemoteConfig;
}

/** Used when no token is supplied at all, so the dev page still renders. */
const PLACEHOLDER_TOKEN = 'demo_session_token';

/** Anything rendered as a URL must be https — this page is publicly reachable. */
function httpsOnly(value: string | null): string | undefined {
  return value && value.startsWith('https://') ? value : undefined;
}

/**
 * Decode the base64 `remoteConfig` param.
 *
 * `atob` yields one char per byte, so UTF-8 has to be decoded explicitly —
 * for pure-ASCII payloads this is byte-identical to `JSON.parse(atob(x))`.
 */
function decodeRemoteConfig(encoded: string): WidgetRemoteConfig | undefined {
  try {
    const binary = atob(encoded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as WidgetRemoteConfig;
  } catch {
    if (__DEV__) {
      console.warn('[PrezioOnboard] Ignoring malformed remoteConfig param.');
    }
    return undefined;
  }
}

/**
 * Read the hosted page's configuration from a query string.
 *
 * Absent params are omitted rather than defaulted, so that `remoteConfig` can
 * still supply them — `init()` merges defaults → remoteConfig → explicit props.
 */
export function parseHostedPageParams(
  search: string,
  fallbacks: HostedPageFallbacks = {},
): HostedPageParams {
  const params = new URLSearchParams(search);

  const sessionId = params.get('sessionId') || fallbacks.sessionId || '';
  const apiBaseUrl = params.get('apiBaseUrl') || fallbacks.apiBaseUrl || '';

  // Unvalidated values would reach `resolveColorScheme` verbatim, where anything
  // that is not 'dark' or 'system' silently renders light — including 'auto'.
  const colorScheme = sanitizeColorScheme(params.get('colorScheme'));
  const primaryColor = params.get('primaryColor');
  const textColor = params.get('textColor');
  const backgroundColor = params.get('backgroundColor');
  const borderRadius = params.get('borderRadius');
  const fontFamily = params.get('fontFamily');
  const logo = httpsOnly(params.get('logo'));

  // Absent means "caller has no opinion" and remoteConfig decides; an explicit
  // `false` is an opt-out and must survive, so key off presence not truthiness.
  const demoMode = params.has('demoMode') ? params.get('demoMode') === 'true' : undefined;
  const upgradeUrl = httpsOnly(params.get('upgradeUrl'));

  const remoteConfigParam = params.get('remoteConfig');
  const remoteConfig = remoteConfigParam ? decodeRemoteConfig(remoteConfigParam) : undefined;
  const locale = params.get('locale');

  return {
    sessionToken: params.get('token') || fallbacks.token || PLACEHOLDER_TOKEN,
    ...(sessionId ? { sessionId } : {}),
    ...(apiBaseUrl ? { apiBaseUrl } : {}),
    ...(demoMode !== undefined ? { demoMode } : {}),
    ...(upgradeUrl ? { upgradeUrl } : {}),
    ...(remoteConfig ? { remoteConfig } : {}),
    ...(locale ? { locale: locale as Locale } : {}),
    theme: {
      ...(colorScheme ? { colorScheme } : {}),
      ...(primaryColor ? { primaryColor } : {}),
      ...(textColor ? { textColor } : {}),
      ...(backgroundColor ? { backgroundColor } : {}),
      ...(borderRadius ? { borderRadius } : {}),
      ...(fontFamily ? { fontFamily } : {}),
      ...(logo ? { logo } : {}),
    },
  };
}
